/**
 * Event utilities — pure helper functions for the events module.
 * Used by both admin and user event components + the useEvents hook.
 */

import { toMillis, isMinorAt } from "@sendly/shared";
import { todayStr, resolveLocale } from "../utils";
import { formatCurrency } from "../competition/settings";

/** Format a YYYY-MM-DD date string as a locale-aware short date (e.g. "júl. 6") */
export function formatEventDate(dateStr, lang = "hu") {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr + "T00:00:00");
    if (Number.isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString(resolveLocale(lang), {
      month: "short",
      day: "numeric",
    });
  } catch {
    return dateStr;
  }
}

/** Format a date range; if start === end returns a single-date label. */
export function formatEventDateRange(start, end, lang = "hu") {
  if (!start) return "";
  if (!end || start === end) return formatEventDate(start, lang);
  return `${formatEventDate(start, lang)} – ${formatEventDate(end, lang)}`;
}

/** Return an array of YYYY-MM-DD strings between startDate and endDate inclusive. */
export function dateKeysBetween(startDate, endDate) {
  if (!startDate || !endDate) return [];
  const start = new Date(startDate + "T00:00:00");
  const end = new Date(endDate + "T00:00:00");
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return [];
  if (end < start) return [];
  const out = [];
  const cur = new Date(start);
  while (cur <= end) {
    const y = cur.getFullYear();
    const m = String(cur.getMonth() + 1).padStart(2, "0");
    const d = String(cur.getDate()).padStart(2, "0");
    out.push(`${y}-${m}-${d}`);
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

/** Sum maxParticipants across all groups. */
export function totalCapacity(groups) {
  if (!Array.isArray(groups)) return 0;
  return groups.reduce((s, g) => s + (Number(g?.maxParticipants) || 0), 0);
}

/**
 * Count confirmed participants per group.
 * Returns a map { groupId: count }.
 */
export function countParticipantsByGroup(participants) {
  const counts = {};
  if (!Array.isArray(participants)) return counts;
  for (const p of participants) {
    if (p?.status !== "cancelled" && p?.groupId) {
      counts[p.groupId] = (counts[p.groupId] || 0) + 1;
    }
  }
  return counts;
}

/**
 * Compute total confirmed across all groups (sum of group counts).
 * Works on either an array of participants or a pre-aggregated counts map.
 */
export function totalConfirmed(participantsOrCounts) {
  if (Array.isArray(participantsOrCounts)) {
    return participantsOrCounts.filter(p => p?.status !== "cancelled").length;
  }
  if (participantsOrCounts && typeof participantsOrCounts === "object") {
    return Object.values(participantsOrCounts).reduce((s, n) => s + (Number(n) || 0), 0);
  }
  return 0;
}

/** Free spots in a specific group. Returns null if group or max missing. */
export function freeSlotsInGroup(group, count) {
  if (!group || group.maxParticipants == null) return null;
  const used = Number(count) || 0;
  return Math.max(0, Number(group.maxParticipants) - used);
}

/**
 * ── A NOTE ON `today` ───────────────────────────────────────────────
 *
 * The four predicates below compare `YYYY-MM-DD` strings, and every caller
 * inside a gym MUST pass that gym's day — `useDateKey(gymMeta?.timezone)` —
 * not the browser's.
 *
 * This module used to carry its own `todayStr()` with no timezone parameter,
 * shadowing the real one in `src/utils.js`. So a member registering from
 * abroad (a holiday, a trip) got their OWN date: registration appeared to open
 * or close a day early or late, on a boundary the gym never chose. The shadow
 * is gone; what remains is the browser fallback, which is correct only where
 * there is no single gym to ask.
 *
 * Determine if an event is "active" — i.e. ongoing or upcoming and published.
 */
export function isEventActive(event, today = todayStr()) {
  if (!event) return false;
  if (event.status !== "published") return false;
  if (!event.endDate) return true;
  return event.endDate >= today;
}

/**
 * @typedef {"open"|"closed"|"notYetOpen"|"deadlinePassed"} EventRegWindowState
 *
 * The registration WINDOW only — the dates plus the manual `registrationClosed`
 * flag. Deliberately status-blind, unlike the competition equivalent
 * (`getRegistrationState` in `competitionRegistration.js`), which answers
 * `notActive` for a draft and stops there.
 *
 * A draft event's card still renders a registration row, and "closed" is the
 * one thing that row must not say: nothing has closed, the organiser simply has
 * not pressed Publish yet. Splitting the window out lets the card preview what
 * the row will read once published, while `isRegistrationOpen` below stays the
 * single gate for anything that actually writes an entry.
 */
export function getEventRegWindowState(event, today = todayStr()) {
  if (!event) return "closed";
  if (event.registrationClosed) return "closed";
  if (event.registrationStart && today < event.registrationStart) return "notYetOpen";
  const deadline = event.registrationDeadline || event.startDate;
  if (deadline && today > deadline) return "deadlinePassed";
  return "open";
}

/** Determine if registration is open right now for an event. */
export function isRegistrationOpen(event, today = todayStr()) {
  if (!event) return false;
  if (event.status !== "published") return false;
  return getEventRegWindowState(event, today) === "open";
}

/**
 * Which parts of the participant form an event's `requireMinorData` flag asks
 * for, given the birth year typed in so far.
 *
 * The flag used to be one yes/no for EVERYBODY: with it on, a 40-year-old
 * signing up for a family climbing day had to name a parent, give that parent's
 * phone number and tick a parental-consent box. The flag now means "find out
 * who is a minor, and collect a guardian only from them" — minority decided by
 * `isMinorAt`, the same cohort rule (age reached in the event's start year) the
 * age groups already use.
 *
 * Three states, not two, and the third is the one worth naming: until a birth
 * year is entered we do not KNOW. The guardian block then stays hidden rather
 * than appearing under the cursor and vanishing again once an adult year is
 * typed. Nothing is lost by waiting — the birth year is required, so the form
 * cannot be submitted from the unknown state anyway.
 *
 * Health fields (allergies, medical notes) are shown to everyone the flag
 * covers but required from nobody: a camp caterer needs an adult's allergy just
 * as much as a child's, and an empty answer is a legitimate answer.
 *
 * @param {object} event
 * @param {number|string|null} birthYear - as typed in the form, may be empty
 * @returns {{birthYearRequired: boolean, showHealth: boolean, showGuardian: boolean, guardianRequired: boolean, isMinor: boolean|null}}
 */
export function eventMinorFields(event, birthYear) {
  if (event?.requireMinorData !== true) {
    return { birthYearRequired: false, showHealth: false, showGuardian: false, guardianRequired: false, isMinor: null };
  }
  const isMinor = isMinorAt(birthYear, event?.startDate);
  return {
    birthYearRequired: true,
    showHealth: true,
    showGuardian: isMinor === true,
    guardianRequired: isMinor === true,
    isMinor,
  };
}

/**
 * What the public sign-up landing's hand-off (`?calFocus=event:…&reg=1|guest`)
 * opens on the event card, decided on the card's LIVE data.
 *
 * - `"guest"`   → the proxy guest form
 * - `"edit"`    → the change-my-entry form (already signed up)
 * - `"register"`→ the own sign-up form (or the waitlist, when full)
 * - `null`      → nothing; the scroll + flash already put them on the card
 *
 * A waitlisted visitor gets `null` for "self": there is no entry to change and
 * a second sign-up is not theirs to make.
 *
 * @param {"self"|"guest"} kind
 * @param {{isRegistered: boolean, regOpen: boolean, canRegisterSelf: boolean, allowProxyRegistration: boolean}} s
 * @returns {"guest"|"edit"|"register"|null}
 */
export function resolveRegisterIntent(kind, s) {
  if (!s) return null;
  if (kind === "guest") return s.allowProxyRegistration && s.regOpen ? "guest" : null;
  if (kind !== "self") return null;
  if (s.isRegistered) return s.regOpen ? "edit" : null;
  return s.canRegisterSelf ? "register" : null;
}

/** Determine if a cancellation can still be made today. */
export function canCancelRegistration(event, today = todayStr()) {
  if (!event) return false;
  const deadline = event.cancellationDeadline || event.startDate;
  if (!deadline) return true;
  return today <= deadline;
}

/**
 * Compute the waitlist position of a specific entry (1-based).
 * Expects entries sorted or unordered; returns null if not on waitlist.
 */
export function waitlistPosition(entries, entryId) {
  if (!Array.isArray(entries) || !entryId) return null;
  const waiting = entries
    .filter(e => e?.status === "waiting")
    .slice()
    .sort((a, b) => {
      const ta = toMillis(a?.waitlistedAt);
      const tb = toMillis(b?.waitlistedAt);
      return ta - tb;
    });
  const idx = waiting.findIndex(e => e.id === entryId);
  return idx >= 0 ? idx + 1 : null;
}

// `toMillis` lives in the shared package (the server reads the same shapes);
// re-exported so the client keeps importing it from here.
export { toMillis };

/**
 * Generate a stable id for inline arrays (groups, schedule days, slots, coaches).
 * Not a security-critical id — just a client-side tag.
 */
export function genId(prefix = "id") {
  const rnd = Math.random().toString(36).slice(2, 10);
  return `${prefix}_${Date.now().toString(36)}_${rnd}`;
}

/** Validate a time "HH:mm" string. */
export function isValidTime(time) {
  if (typeof time !== "string") return false;
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(time);
}

/**
 * Return true if the event has at least one program day with at least one
 * configured slot. When this is true, the per-day program is the source of
 * truth for timing and the generic `dailyStartTime`/`dailyEndTime` should
 * be hidden as redundant.
 */
export function hasScheduleSlots(event) {
  const days = event?.schedule;
  if (!Array.isArray(days)) return false;
  return days.some(d => Array.isArray(d?.slots) && d.slots.length > 0);
}

/**
 * Format the generic daily times as "HH:mm–HH:mm". Returns an empty
 * string if neither time is set. Useful as a fallback header label when
 * the event has no per-day program slots yet.
 */
export function formatDailyTimeRange(event) {
  const start = event?.dailyStartTime || "";
  const end = event?.dailyEndTime || "";
  if (!start && !end) return "";
  if (start && end) return `${start}–${end}`;
  return start || end;
}

/** Return true if two slots on the same day overlap. */
export function slotsOverlap(a, b) {
  if (!a?.startTime || !a?.endTime || !b?.startTime || !b?.endTime) return false;
  return a.startTime < b.endTime && b.startTime < a.endTime;
}

/**
 * Format a currency amount like "45 000 HUF" — with the ONE money formatter the
 * fee ladder and the competition fees print with (`formatCurrency`: HUF grouped
 * by thousands from 1 000 up, other currencies with two decimals). This used to
 * be `toLocaleString("hu-HU")`, which leaves a four-digit amount ungrouped: the
 * same card printed the ladder's "3 000 HUF" and, once the viewer was in, the fee
 * row's "3000 HUF" (found by the J09 browser test) — and it turned a decimal
 * comma into a space ("12 5 EUR").
 */
export function formatEventFee(amount, currency) {
  if (amount == null || !Number.isFinite(Number(amount))) return "";
  const cur = currency || "HUF";
  return `${formatCurrency(Number(amount), cur)} ${cur}`;
}

/**
 * Given an event + today, return a high-level registration status token.
 * Used by the EventCard status chip.
 * Returns one of: "regOpen" | "regClosed" | "full" | "waitlist" | "cancelled"
 */
export function getRegistrationStatus(event, counts, today = todayStr()) {
  if (!event) return "regClosed";
  if (event.status === "cancelled") return "cancelled";
  if (!isRegistrationOpen(event, today)) return "regClosed";
  const total = totalCapacity(event.groups || []);
  const confirmed = totalConfirmed(counts);
  if (total > 0 && confirmed >= total) {
    return event.waitlistEnabled ? "waitlist" : "full";
  }
  return "regOpen";
}

// The fresh event skeleton is the shared package's (`packages/shared/src/eventDraft.js`):
// the MCP server builds event drafts from it too. Re-exported for the editor.
export { makeEmptyEvent } from "@sendly/shared";
